import React, {useState} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';

import {Icon} from '@stubhub/bamboo';
import CBTRedirectionModal, {
  CBT_MODAL_MODES,
  getRedirectURL,
  MODAL_TRACK_NAME,
} from '@stubhub/react-cbt-redirection-modal';
import FormattedLink from '@stubhub/react-formatted-link';

import '../styles/index.scss';

const CBTRedirection = ({
  auto,
  isEnabled,
  uri,
  keepSearch,
  countryCode,
  location,
  mode,
  className,
  modalClassName,
  children,
  linkRef,
  modalRef,
  onClick,
  onClose,
  showIcon = true,
}) => {
  const showModal = isEnabled && !!countryCode && countryCode.toUpperCase() !== 'GB';
  const finalUrl = showModal ? getRedirectURL(uri, location, keepSearch, null, MODAL_TRACK_NAME) : uri;

  const [isOpen, setIsOpen] = useState(auto && showModal);

  const handleClick = (evt) => {
    evt && evt.stopPropagation();
    onClick && onClick(evt);

    if (showModal) {
      setIsOpen(true);
      evt && evt.preventDefault();
    }
  };

  const handleClose = (evt) => {
    setIsOpen(false);
    onClose && onClose(evt);
  };

  return (
    <>
      {isOpen && (
        <CBTRedirectionModal
          className={modalClassName}
          uri={uri}
          mode={mode}
          isOpen
          location={location}
          keepSearch={keepSearch}
          modalRef={modalRef}
          onClose={handleClose}
        />
      )}
      {!auto && (
        <FormattedLink
          linkRef={linkRef}
          href={finalUrl}
          onClick={handleClick}
          className={cs('cbt-redirection__link', className)}
          data-testid="cbt-redirection-link"
          data-url={finalUrl}
        >
          {children}
          {showModal && showIcon && <Icon name="ExternalLink" size="sm" className="cbt-redirection__icon" />}
        </FormattedLink>
      )}
    </>
  );
};

CBTRedirection.propTypes = {
  /** Should redirection modal be shown automatically? */
  auto: PropTypes.bool,

  /** Is redirection enabled? */
  isEnabled: PropTypes.bool,

  /** Location where the modal has been shown */
  location: PropTypes.oneOf(['home', 'search', 'category', 'grouping', 'city', 'venue', 'performer', 'event'])
    .isRequired,

  /** Redirection uri. If not provided*/
  uri: PropTypes.string,

  /** Should search params be maintained when redirecting? */
  keepSearch: PropTypes.bool,

  /** Country code that should be checked along with isEnabled */
  countryCode: PropTypes.string,

  /** Ref to the link element */
  linkRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),

  /** Ref to the cbt modal */
  modalRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),

  /** Children node */
  children: PropTypes.node,

  /** Custom classname for the link */
  className: PropTypes.string,

  /** Custom classname for the modal */
  modalClassName: PropTypes.string,

  /** Modal mode, optional or mandatory */
  mode: PropTypes.oneOf([CBT_MODAL_MODES.OPTIONAL, CBT_MODAL_MODES.MANDATORY]),

  /** Callback invoked when link is clicked */
  onClick: PropTypes.func,

  /** Callback invoked when modal is closed */
  onClose: PropTypes.func,

  /** Should show the external link icon? */
  showIcon: PropTypes.bool,
};

export default CBTRedirection;
